import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { hasRole, useAuth } from '../lib/auth';
import { FirstBatch } from '../ui/first-batch';
import { ErrorBox, Page, Section, useApi } from '../ui/kit';

// The approval link request_launch gives an MCP client or the API (src/outbound/launch.ts): nothing of the campaign is
// sent until a signed-in owner or admin opens it, reads the checks and the first emails (the same review as the
// campaign page: approve, edit or remove each) and approves. Approving approves the first emails still waiting and
// starts the campaign on autopilot. The link stops approving when it expires or is decided.

type LaunchView = {
  request_id: string; status: 'pending' | 'approved' | 'declined' | 'cancelled' | 'expired'; expires_at: string; requested_at: string; decided_at: string | null;
  requested_by: { type: 'api_key' | 'user'; name: string; client: string | null };
  campaign: { id: string; name: string; status: string; first_batch_status: string; leads: number; steps: number };
  missing: Array<{ what: string; detail: string; next: string }>;
  sending: { summary: string; ready_around: string | null; mailboxes: number };
  can_approve: boolean;
};

const STATUS_TEXT: Record<LaunchView['status'], string> = {
  pending: 'Waiting for you', approved: 'Approved: the campaign started', declined: 'Declined: nothing was sent',
  cancelled: 'Replaced by a newer request', expired: 'Expired: it approves nothing now',
};
const when = (s: string) => new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function LaunchApprove() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { me } = useAuth();
  const q = useApi<LaunchView>(`/portal/launch-requests/${id}`, { refetchMs: 30_000 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);
  if (q.isLoading) return <Page title="Launch"><p role="status">Loading…</p></Page>;
  if (q.error || !q.data) return <Page title="Launch"><ErrorBox error={q.error} /><p className="muted small">Only an owner or admin of this workspace opens launch links.</p></Page>;
  const r = q.data;
  const canDecide = me ? hasRole(me, 'owner', 'admin') : false;
  const decide = async (what: 'approve' | 'decline') => {
    if (what === 'decline' && !confirm(`Decline starting “${r.campaign.name}”? Nothing is sent; the developer can ask again.`)) return;
    setBusy(true); setErr(null);
    try {
      const out = await api<{ first_emails_approved?: number }>(`/portal/launch-requests/${r.request_id}/${what}`, { method: 'POST' });
      setDone(what === 'approve' ? `Started. ${out.first_emails_approved ? `The ${out.first_emails_approved} first email${out.first_emails_approved === 1 ? '' : 's'} you saw are approved and go out within your daily limits.` : 'Its first emails wait for your review here as its first contacts join.'}` : 'Declined. Nothing was sent.');
      await qc.invalidateQueries();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  const who = r.requested_by.type === 'api_key' ? `the API key “${r.requested_by.name}”${r.requested_by.client === 'mcp' ? ' from an MCP client (Claude, Cursor…)' : ''}` : r.requested_by.name;
  return (
    <Page title={`Start “${r.campaign.name}”?`}>
      <div className="pv-head">
        <span className={`chip ${r.status === 'approved' ? 'done' : r.status === 'pending' ? 'next' : ''}`}>{STATUS_TEXT[r.status]}</span>
        <span className="muted small">Asked by {who} on {when(r.requested_at)}{r.status === 'pending' ? ` · the link works until ${when(r.expires_at)}` : ''}</span>
      </div>
      <Section title="What you are approving">
        <p className="small">Starting this campaign: {r.campaign.steps} email{r.campaign.steps === 1 ? '' : 's'} per contact, {r.campaign.leads} contact{r.campaign.leads === 1 ? '' : 's'} so far, sent from {r.sending.mailboxes} mailbox{r.sending.mailboxes === 1 ? '' : 'es'} ({r.sending.summary}). Autopilot sends within your daily limits; you can pause it any time on the <Link to={`/campaigns/${r.campaign.id}`}>campaign page</Link>.</p>
        {r.missing.length ? <>
          <p className="small"><b>It can&rsquo;t start yet:</b></p>
          <ul className="small">{r.missing.map((m) => <li key={m.detail}>{m.detail}. <span className="muted">{m.next}.</span></li>)}</ul>
        </> : null}
        {r.status === 'pending' && !r.missing.length && !r.can_approve ? <p className="muted small" role="status">Outlevo is building it in Smartlead (unstarted); this takes a minute or two.</p> : null}
      </Section>
      <FirstBatch campaignId={r.campaign.id} />
      {done ? <p className="wg-done" role="status">✓ {done}</p> : r.status === 'pending' ? (
        canDecide ? <div className="wg-actions">
          <button type="button" className="btn btn-primary" disabled={busy || !r.can_approve} onClick={() => void decide('approve')}>{busy ? 'Starting…' : r.campaign.first_batch_status === 'pending' ? 'Approve the first emails and start' : 'Approve and start'}</button>
          <button type="button" className="btn" disabled={busy} onClick={() => void decide('decline')}>Decline</button>
        </div> : <p className="muted small">An owner or admin approves a launch.</p>
      ) : r.status === 'expired' ? <p className="muted small">Ask for a new link (request_launch again), or start the campaign from its page.</p> : null}
      {err ? <ErrorBox error={err} doing={'do that'} /> : null}
    </Page>
  );
}
